import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { GROUP_PRIZES } from "@/lib/constants";
import { loadTopRanking, loadGroupRankings, type RankRow, type GroupRankingRow, type TopRanking } from "@/lib/ranking";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import confetti from "canvas-confetti";
import { Link, useSearchParams } from "react-router-dom";
import { loadMyGroups } from "@/lib/groups";

import { UserAvatar } from "@/components/UserAvatar";
import { InviteButton } from "@/components/InviteButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RacePodium } from "@/components/RacePodium";
import { SignupBonusBanner } from "@/components/SignupBonusBanner";
import { SinglePrizeBanner } from "@/components/SinglePrizeBanner";
import { TrendingUp, TrendingDown, Users } from "lucide-react";
import { useLiveBonus } from "@/hooks/useLiveBonus";
import { GroupLogo } from "@/components/GroupLogo";

const RANKS_KEY = "ranking_prev_positions_v1";
const GROUP_RANKS_KEY = "group_ranking_prev_positions_v1";

function loadPrev<T extends string>(key: string): Record<T, number> {
  try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {} as any; }
}

export default function Ranking() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "single" ? "single" : "groups";
  const [rows, setRows] = useState<RankRow[]>([]);
  const [me, setMe] = useState<TopRanking["me"]>(null);
  const [total, setTotal] = useState(0);
  const [groupRows, setGroupRows] = useState<GroupRankingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [myGroups, setMyGroups] = useState<any[]>([]);
  const [myGroupIds, setMyGroupIds] = useState<Set<string>>(new Set());
  const [prevRanks, setPrevRanks] = useState<Record<string, number>>({});
  const [prevGroupRanks, setPrevGroupRanks] = useState<Record<string, number>>({});
  const celebratedRef = useRef(false);
  const liveBonus = useLiveBonus();

  useEffect(() => { document.title = "Ranking – Englputzeder Tippspiel"; }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const previous = loadPrev<string>(RANKS_KEY);
      const previousG = loadPrev<string>(GROUP_RANKS_KEY);
      // Top 100 only — with thousands of tippers the full list was a
      // multi-MB payload AND thousands of animated DOM rows (mobile
      // browsers froze). Own position still shows via `me`/`total` below.
      const [top, gArr, groups] = await Promise.all([
        loadTopRanking(100),
        loadGroupRankings(),
        user ? loadMyGroups(user.id) : Promise.resolve([]),
      ]);
      const arr = top.rows;
      if (!mounted) return;
      setPrevRanks(previous);
      setPrevGroupRanks(previousG);
      setRows(arr);
      setMe(top.me);
      setTotal(top.total);
      setGroupRows(gArr);
      setMyGroups(groups);
      setMyGroupIds(new Set(groups.map((g: any) => g.id)));
      setLoading(false);
      setTimeout(() => {
        const m: Record<string, number> = {};
        arr.forEach((r, i) => { m[r.user_id] = i + 1; });
        localStorage.setItem(RANKS_KEY, JSON.stringify(m));
        const gm: Record<string, number> = {};
        gArr.forEach((g) => { gm[g.group_id] = g.rank; });
        localStorage.setItem(GROUP_RANKS_KEY, JSON.stringify(gm));
      }, 1500);
    })();
    return () => { mounted = false; };
  }, [user]);

  const myBestGroupIdx = useMemo(() => {
    if (myGroupIds.size === 0) return -1;
    return groupRows.findIndex(g => myGroupIds.has(g.group_id));
  }, [groupRows, myGroupIds]);

  const groupPodium = groupRows.slice(0, 3);

  useEffect(() => {
    if (celebratedRef.current) return;
    if (myBestGroupIdx < 0 || myBestGroupIdx > 2) return;
    celebratedRef.current = true;
    const colors = myBestGroupIdx === 0 ? ["#FFD24A", "#E8A93B", "#FFFFFF"] : myBestGroupIdx === 1 ? ["#D4D7DC", "#9AA0A8", "#FFFFFF"] : ["#D08A4F", "#A05A2A", "#FFFFFF"];
    const end = Date.now() + 900;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [myBestGroupIdx]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-extrabold text-3xl">Ranking 🏆</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Die 3 punktstärksten <b>Tippgruppen</b> gewinnen Englputzeder-Sponsoring (3.000 € / 1.500 € / 500 €).
          Plus: <b>Wellnessurlaub für 2</b> für die/den Sieger:in der Einzelwertung.
        </p>
      </div>

      {/* Live banner — shown only while at least one match is in play.
          Surfaces the provisional points the user is earning right now
          without writing anything to the DB. */}
      {liveBonus.hasLive && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-lg p-3 flex items-center gap-3 shadow-pop">
          <div className="text-2xl">✨</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Live · {liveBonus.liveGames.length} {liveBonus.liveGames.length === 1 ? "Spiel" : "Spiele"}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {liveBonus.liveGames.map(g => `${g.homeFlag} ${g.homeTeam} ${g.liveHomeScore ?? "?"}:${g.liveAwayScore ?? "?"} ${g.awayTeam} ${g.awayFlag}`).join(" · ")}
            </div>
          </div>
          {user && (liveBonus.perUser[user.id] ?? 0) > 0 && (
            <div className="text-right shrink-0">
              <div className="font-display font-extrabold text-xl tabular-nums text-foreground">+{liveBonus.perUser[user.id]}</div>
              <div className="text-[9px] font-bold uppercase text-muted-foreground">live</div>
            </div>
          )}
        </div>
      )}

      {/* My groups quick access */}
      {myGroups.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">Meine Gruppen-Rankings</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {myGroups.map(g => (
              <Link key={g.id} to={`/gruppe/${g.slug}`} className="shrink-0 bg-card rounded-lg shadow-card p-2 flex items-center gap-2 tap min-w-[140px]">
                <GroupLogo url={g.logo_url} size={32} />
                <div className="min-w-0">
                  <div className="text-xs font-bold truncate">{g.name}</div>
                  <div className="text-[10px] text-muted-foreground">{g.member_count} 👥</div>
                </div>
              </Link>
            ))}
            <Link to="/gruppen" className="shrink-0 bg-card rounded-lg shadow-card p-2 flex items-center justify-center text-xs font-bold text-muted-foreground tap min-w-[80px]">
              + mehr
            </Link>
          </div>
        </div>
      )}

      {/* Race um Gruppen-Sponsoring */}
      <div className="bg-card rounded-lg shadow-card p-4">
        <RacePodium />
      </div>

      {/* Anmeldebonus */}
      <SignupBonusBanner />


      <Tabs value={initialTab} onValueChange={(v) => setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true })} className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="groups">👥 Gruppen</TabsTrigger>
          <TabsTrigger value="single">🧍 Einzeln</TabsTrigger>
        </TabsList>

        {/* GROUP RANKING */}
        <TabsContent value="groups" className="space-y-4">
          {/* Podium */}
          {groupPodium.length === 3 && (
            <div className="grid grid-cols-3 items-end gap-2">
              {[1,0,2].map((order, i) => {
                const g = groupPodium[order];
                const heights = ["h-20","h-28","h-16"];
                const tones = ["bg-silver","bg-gold","bg-bronze"] as const;
                const medals = ["🥈","🥇","🥉"];
                const prize = GROUP_PRIZES[order];
                return (
                  <motion.div key={g.group_id} initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{delay: i*0.1}} className="flex flex-col items-center">
                    <div className="text-3xl mb-1">{medals[i]}</div>
                    <Link to={`/gruppe/${g.slug}`} className="flex flex-col items-center min-w-0 w-full">
                      <GroupLogo url={g.logo_url} size={48} />
                      <div className="text-xs font-bold truncate max-w-full mt-1">{g.name}</div>
                    </Link>
                    <div className="text-[10px] font-bold text-accent-foreground bg-accent rounded-pill px-1.5 py-0.5 mt-0.5 tabular-nums">{prize.short}</div>
                    <div className={`w-full ${heights[i]} ${tones[i]} rounded-t-md mt-1 flex flex-col items-center justify-center font-display font-extrabold leading-tight`}>
                      <span className="text-lg tabular-nums">{g.total_points}</span>
                      <span className="text-[9px] font-bold opacity-80">Pkt</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="bg-card rounded-lg shadow-card overflow-hidden">
            {loading && <div className="p-6 text-center text-muted-foreground">Lade Ranking …</div>}
            {!loading && groupRows.length === 0 && <div className="p-6 text-center text-muted-foreground">Noch keine Tippgruppen.</div>}
            <LayoutGroup>
              <AnimatePresence initial={false}>
                {groupRows.map((g, i) => {
                  const mine = myGroupIds.has(g.group_id);
                  const prize = i < 3 ? GROUP_PRIZES[i] : null;
                  const oldRank = prevGroupRanks[g.group_id];
                  const delta = oldRank ? oldRank - g.rank : 0;
                  return (
                    <motion.div key={g.group_id} layout
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ layout: { type: "spring", stiffness: 320, damping: 30 } }}
                      className={`flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 ${mine ? "bg-accent/30" : ""}`}
                    >
                      <div className={`w-14 shrink-0 text-center font-display font-extrabold text-xs tabular-nums ${i<3 ? "text-foreground" : "text-muted-foreground"}`}>#{g.rank}</div>
                      <GroupLogo url={g.logo_url} size={36} />
                      <Link to={`/gruppe/${g.slug}`} className="flex-1 min-w-0 tap">
                        <div className="font-bold text-sm truncate flex items-center gap-1.5">
                          <span className="truncate">{g.name}</span>
                          {mine && <span className="text-[10px] text-accent-foreground bg-accent rounded-pill px-1.5 py-0.5 shrink-0">MEINE</span>}
                          {delta > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-success bg-success/10 rounded-pill px-1.5 py-0.5 shrink-0">
                              <TrendingUp className="h-3 w-3" /> +{delta}
                            </span>
                          )}
                          {delta < 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-destructive bg-destructive/10 rounded-pill px-1.5 py-0.5 shrink-0">
                              <TrendingDown className="h-3 w-3" /> {delta}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" /> {g.member_count} Mitglieder
                        </div>
                      </Link>
                      {prize && (
                        <div className={`hidden xs:flex items-center gap-1 rounded-pill px-2 py-0.5 text-[10px] font-bold ${prize.tone === "gold" ? "bg-gold" : prize.tone === "silver" ? "bg-silver" : "bg-bronze"}`}>
                          <span>{prize.icon}</span>
                          <span className="tabular-nums">{prize.short}</span>
                        </div>
                      )}
                      <div className="text-right">
                        <div className="font-display font-extrabold text-lg tabular-nums flex items-center justify-end gap-1.5">
                          {g.total_points}
                          {(liveBonus.perGroup[g.group_id] ?? 0) > 0 && (
                            <span className="text-[10px] font-bold bg-gold text-foreground rounded-pill px-1.5 py-0.5 animate-pulse">
                              +{liveBonus.perGroup[g.group_id]}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {(liveBonus.perGroup[g.group_id] ?? 0) > 0 ? "Punkte · live ✨" : "Punkte"}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </LayoutGroup>
          </div>

          <div className="bg-card rounded-lg shadow-card p-3 text-[11px] text-muted-foreground border border-border">
            ℹ️ Die Gruppenpunkte ergeben sich aus der <b>Summe</b> aller Tipp- und Bonuspunkte der Mitglieder. Je mehr aktive Tipper, desto höher die Chance.
          </div>
        </TabsContent>

        {/* SINGLE RANKING (no prizes) */}
        <TabsContent value="single" className="space-y-3">
          <SinglePrizeBanner variant="ranking" />
          <div className="text-[11px] text-muted-foreground px-1">
            Die/der <b>#1 der Einzelwertung</b> gewinnt den Wellnessurlaub. Gruppen-Sponsoring siehe Tab „Gruppen".
          </div>
          <div className="bg-card rounded-lg shadow-card overflow-hidden">
            {loading && <div className="p-6 text-center text-muted-foreground">Lade Ranking …</div>}
            {!loading && rows.length === 0 && <div className="p-6 text-center text-muted-foreground">Noch keine Tipper.</div>}
            <LayoutGroup>
              <AnimatePresence initial={false}>
                {rows.map((r, i) => {
                  const me = user?.id === r.user_id;
                  const newRank = i + 1;
                  const oldRank = prevRanks[r.user_id];
                  const delta = oldRank ? oldRank - newRank : 0;
                  const justJumped = Math.abs(delta) >= 1;
                  const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                  return (
                    <motion.div key={r.user_id} layout
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        backgroundColor: justJumped
                          ? delta > 0
                            ? ["hsl(var(--accent) / 0.45)", "hsl(var(--accent) / 0)"]
                            : ["hsl(var(--destructive) / 0.18)", "hsl(var(--destructive) / 0)"]
                          : undefined,
                      }}
                      transition={{ layout: { type: "spring", stiffness: 320, damping: 30 }, backgroundColor: { duration: 2.4, times: [0, 1] } }}
                      className={`flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 ${me ? "bg-accent/30" : ""}`}
                    >
                      <div className={`w-14 shrink-0 text-center font-display font-extrabold tabular-nums ${medal ? "text-base" : "text-xs"} ${i<3 ? "text-foreground" : "text-muted-foreground"}`}>
                        {medal ?? `#${newRank}`}
                      </div>
                      <UserAvatar url={r.avatar_url} emoji={r.emoji} size={36} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate flex items-center gap-1.5">
                          <span className="truncate">{r.username}</span>
                          {me && <span className="text-[10px] text-accent-foreground bg-accent rounded-pill px-1.5 py-0.5 shrink-0">DU</span>}
                          {i === 0 && (
                            <span className="text-[10px] font-bold rounded-pill px-1.5 py-0.5 shrink-0 bg-gold">🧖 Wellness für 2</span>
                          )}
                          {justJumped && delta > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-success bg-success/10 rounded-pill px-1.5 py-0.5 shrink-0">
                              <TrendingUp className="h-3 w-3" /> +{delta}
                            </span>
                          )}
                          {justJumped && delta < 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-destructive bg-destructive/10 rounded-pill px-1.5 py-0.5 shrink-0">
                              <TrendingDown className="h-3 w-3" /> {delta}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {r.exact} 🎯 · {r.correct} ✓
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-extrabold text-lg tabular-nums flex items-center justify-end gap-1.5">
                          {r.points}
                          {(liveBonus.perUser[r.user_id] ?? 0) > 0 && (
                            <span className="text-[10px] font-bold bg-gold text-foreground rounded-pill px-1.5 py-0.5 animate-pulse">
                              +{liveBonus.perUser[r.user_id]}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {(liveBonus.perUser[r.user_id] ?? 0) > 0 ? "Punkte · live ✨" : "Punkte"}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </LayoutGroup>
          </div>

          {total > rows.length && (
            <div className="text-center text-[11px] text-muted-foreground">
              Top {rows.length} von {total} Tippern
            </div>
          )}
          {me && (
            <div className="text-center text-sm text-muted-foreground">
              Dein Rang: <b className="text-foreground">#{me.rank}</b> von {total} · {me.points} Punkte
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="bg-card rounded-lg shadow-card p-4 text-center space-y-2">
        <div className="text-sm font-bold">Mehr Tipper in deiner Gruppe = mehr Punkte 📈</div>
        <p className="text-xs text-muted-foreground">Lade Freunde ein — eure Gruppe profitiert direkt.</p>
        <InviteButton variant="cta" label="Freunde per WhatsApp einladen 📲" />
      </div>
    </div>
  );
}
